<template>
  <section id="section-attendance" class="attendance-section">
    <header class="attendance-section__header">
      <h2>{{ $t('attendance.title') }}</h2>
      <p>{{ $t('attendance.desc') }}</p>
    </header>

    <div class="attendance-grid">
      <article class="attendance-card attendance-card--calendar">
        <div class="attendance-calendar__nav">
          <button
            type="button"
            class="attendance-calendar__nav-btn"
            :aria-label="$t('attendance.prevMonth')"
            :disabled="loading"
            @click="shiftMonth(-1)"
          >&lt;</button>
          <strong class="attendance-calendar__month">{{ monthLabel }}</strong>
          <button
            type="button"
            class="attendance-calendar__nav-btn"
            :aria-label="$t('attendance.nextMonth')"
            :disabled="loading"
            @click="shiftMonth(1)"
          >&gt;</button>
        </div>

        <div class="attendance-calendar__weekdays" role="row">
          <span
            v-for="(label, index) in weekdayLabels"
            :key="`weekday-${index}`"
            class="attendance-calendar__weekday"
            :class="{ 'attendance-calendar__weekday--sun': index === 0, 'attendance-calendar__weekday--sat': index === 6 }"
          >{{ label }}</span>
        </div>

        <div
          class="attendance-calendar__grid"
          :class="{ 'attendance-calendar__grid--loading': loading }"
          role="grid"
          :aria-label="monthLabel"
        >
          <div
            v-for="(day, index) in calendarCells"
            :key="`cell-${viewYear}-${viewMonth}-${index}`"
            class="attendance-calendar__cell"
            :class="cellClass(day)"
            role="gridcell"
            :aria-label="day ? cellAriaLabel(day) : undefined"
          >
            <div v-if="day" class="bg-att-cell__inner">
              <span
                class="bg-att-cell__day"
                :class="{ 'bg-att-cell__day--today': isToday(day) }"
              >
                <template v-if="isToday(day)">{{ $t('attendance.today') }}</template>
                <template v-else>{{ day }}</template>
              </span>

              <span
                v-if="isAttended(day)"
                class="bg-att-cell__check"
                :title="$t('attendance.checkedShort')"
              >
                <span class="bg-att-cell__check-box" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M6.5 12.2 10.2 16 17.5 8.2"
                      stroke="currentColor"
                      stroke-width="3"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </span>
                <span class="bg-att-cell__check-text">{{ $t('attendance.checkedShort') }}</span>
              </span>
            </div>
          </div>
        </div>

        <button
          type="button"
          class="attendance-checkin-btn"
          :disabled="loading || submitting || todayCheckedIn || !isViewingCurrentMonth"
          @click="checkIn"
        >
          {{ checkInButtonLabel }}
        </button>
      </article>

      <div class="attendance-side">
        <article class="attendance-card attendance-card--status">
          <p class="attendance-status">
            <span>{{ $t('attendance.streakLabel') }}:</span>
            <strong>{{ streakDisplay }}</strong>
          </p>
        </article>

        <article class="attendance-card attendance-card--info">
          <h3 class="attendance-info__title">{{ $t('attendance.rewardTitle') }}</h3>
          <ul class="attendance-info__list">
            <li v-for="(item, index) in rewardItems" :key="`reward-${index}`">{{ item }}</li>
          </ul>

          <h3 class="attendance-info__title attendance-info__title--notice">{{ $t('attendance.noticeTitle') }}</h3>
          <ul class="attendance-info__list attendance-info__list--notice">
            <li v-for="(item, index) in noticeItems" :key="`notice-${index}`">{{ item }}</li>
          </ul>
        </article>
      </div>
    </div>
  </section>
</template>

<script>
import { WalletService } from '@/services'
import { showToast } from '@/utils/toast'
import { formatCurrencyAmount } from '@/utils/format'
import { resolveSiteCurrency } from '@/utils/siteBrand'
import { resolveSiteAttendanceMinAmount, formatSiteWithdrawLimitPercent } from '@/utils/paymentMethod'
import {
  buildAttendedDateSet,
  buildCalendarCells,
  calcConsecutiveStreak,
  formatAttendanceMonthLabel,
  formatDateKey,
  formatMonthKey
} from '@/utils/attendance'

export default {
  name: 'BlackGoldAttendancePage',
  data () {
    const now = new Date()
    return {
      viewYear: now.getFullYear(),
      viewMonth: now.getMonth(),
      attendedSet: new Set(),
      loading: false,
      submitting: false
    }
  },
  computed: {
    monthKey () {
      return formatMonthKey(this.viewYear, this.viewMonth)
    },
    monthLabel () {
      return formatAttendanceMonthLabel(this.viewYear, this.viewMonth, this.$i18n.locale)
    },
    weekdayLabels () {
      const labels = this.$t('attendance.weekdays')
      if (Array.isArray(labels)) return labels
      return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    },
    calendarCells () {
      return buildCalendarCells(this.viewYear, this.viewMonth)
    },
    todayKey () {
      const now = new Date()
      return formatDateKey(now.getFullYear(), now.getMonth(), now.getDate())
    },
    isViewingCurrentMonth () {
      const now = new Date()
      return this.viewYear === now.getFullYear() && this.viewMonth === now.getMonth()
    },
    todayCheckedIn () {
      return this.attendedSet.has(this.todayKey)
    },
    streakCount () {
      return calcConsecutiveStreak(this.attendedSet, this.viewYear, this.viewMonth)
    },
    streakDisplay () {
      if (this.streakCount <= 0) return this.$t('attendance.notChecked')
      return this.$t('attendance.streakDays', { days: this.streakCount })
    },
    checkInButtonLabel () {
      if (this.loading) return this.$t('attendance.loading')
      if (this.todayCheckedIn) return this.$t('attendance.checkInDone')
      return this.$t('attendance.checkIn')
    },
    minDepositLabel () {
      const amount = resolveSiteAttendanceMinAmount(this.$store.state.siteInfo)
      const currency = resolveSiteCurrency(this.$store.state.siteInfo)
      return formatCurrencyAmount(amount, currency)
    },
    withdrawLimitPercentLabel () {
      return formatSiteWithdrawLimitPercent(this.$store.state.siteInfo)
    },
    rewardItems () {
      return [
        this.$t('attendance.reward1'),
        this.$t('attendance.reward2'),
        this.$t('attendance.reward3'),
        this.$t('attendance.reward4')
      ]
    },
    noticeItems () {
      return [
        this.$t('attendance.notice1', { amount: this.minDepositLabel }),
        this.$t('attendance.notice2', { percent: this.withdrawLimitPercentLabel }),
        this.$t('attendance.notice3'),
        this.$t('attendance.notice4'),
        this.$t('attendance.notice5')
      ]
    }
  },
  watch: {
    monthKey: {
      immediate: true,
      handler () {
        this.loadMonthAttendance()
      }
    }
  },
  methods: {
    reload () {
      this.loadMonthAttendance()
    },
    async loadMonthAttendance () {
      this.loading = true
      try {
        const res = await WalletService.getAttendancesInfo(this.monthKey)
        const body = res && res.data
        if (!body || body.code !== 0 || !body.data) {
          showToast(this.$t('attendance.loadError'))
          this.attendedSet = new Set()
          return
        }
        this.attendedSet = buildAttendedDateSet(body.data.dates)
      } catch (err) {
        this.attendedSet = new Set()
        showToast(this.$t('attendance.loadError'))
      } finally {
        this.loading = false
      }
    },
    shiftMonth (delta) {
      const date = new Date(this.viewYear, this.viewMonth + delta, 1)
      this.viewYear = date.getFullYear()
      this.viewMonth = date.getMonth()
    },
    isToday (day) {
      const now = new Date()
      return (
        this.viewYear === now.getFullYear() &&
        this.viewMonth === now.getMonth() &&
        day === now.getDate()
      )
    },
    isAttended (day) {
      return this.attendedSet.has(formatDateKey(this.viewYear, this.viewMonth, day))
    },
    cellClass (day) {
      if (!day) return 'attendance-calendar__cell--empty'
      const attended = this.isAttended(day)
      const today = this.isToday(day)
      return {
        'attendance-calendar__cell--today': today && !attended,
        'attendance-calendar__cell--checked': attended,
        'attendance-calendar__cell--today-checked': today && attended
      }
    },
    cellAriaLabel (day) {
      const dateKey = formatDateKey(this.viewYear, this.viewMonth, day)
      if (this.isAttended(day)) {
        return this.$t('attendance.checkedAria', { date: dateKey })
      }
      if (this.isToday(day)) {
        return this.$t('attendance.todayAria', { date: dateKey })
      }
      return dateKey
    },
    async checkIn () {
      if (this.loading || this.submitting || this.todayCheckedIn || !this.isViewingCurrentMonth) return

      this.submitting = true
      try {
        const res = await WalletService.procAttendance()
        const body = res && res.data
        if (!body || body.code !== 0 || !body.data) {
          showToast(this.$t('attendance.checkInFail'))
          return
        }

        const state = Number(body.data.state)
        if (state === 1) {
          showToast(this.$t('attendance.alreadyChecked'))
          await this.loadMonthAttendance()
          return
        }
        if (state === 2) {
          showToast(this.$t('attendance.notEligible', { amount: this.minDepositLabel }))
          return
        }
        if (state !== 0) {
          showToast(this.$t('attendance.checkInFail'))
          return
        }

        showToast(this.$t('attendance.checkInSuccess'))
        await this.loadMonthAttendance()
      } catch (err) {
        showToast(this.$t('attendance.checkInFail'))
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.bg-att-cell__inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  min-height: 64px;
  padding: 8px 4px;
  box-sizing: border-box;
}

.bg-att-cell__day {
  font-size: 15px;
  font-weight: 900;
  line-height: 1;
  color: #f2eee3;
}

.bg-att-cell__day--today {
  color: #b8f0c8;
  font-size: 12px;
  letter-spacing: -.02em;
}

.attendance-calendar__cell--checked .bg-att-cell__day,
.attendance-calendar__cell--today-checked .bg-att-cell__day {
  color: #fff3c4;
}

.attendance-calendar__cell--today-checked .bg-att-cell__day--today {
  color: #d8ffe3;
}

.bg-att-cell__check {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
}

.bg-att-cell__check-box {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 2px solid #fff4c8;
  background: linear-gradient(145deg, #fff8df 0%, #ffd85d 45%, #d7a018 100%);
  color: #2f1800;
  box-shadow:
    0 0 0 1px rgba(255, 236, 170, .45),
    0 4px 12px rgba(231, 194, 93, .45),
    inset 0 1px 0 rgba(255, 255, 255, .5);
}

.bg-att-cell__check-box svg {
  display: block;
  width: 18px;
  height: 18px;
}

.bg-att-cell__check-text {
  font-size: 10px;
  font-weight: 900;
  line-height: 1;
  letter-spacing: .02em;
  color: #ffd85d;
  text-shadow: 0 0 8px rgba(255, 214, 93, .35);
}

.attendance-calendar__cell--checked {
  border-color: rgba(255, 214, 93, .62) !important;
  background:
    radial-gradient(circle at 50% 0%, rgba(255, 236, 170, .3), transparent 62%),
    linear-gradient(165deg, rgba(64, 50, 22, .96) 0%, rgba(18, 15, 10, .98) 100%) !important;
  box-shadow:
    inset 0 1px 0 rgba(255, 236, 170, .24),
    0 0 0 1px rgba(255, 214, 93, .18),
    0 6px 18px rgba(231, 194, 93, .2) !important;
}

.attendance-calendar__cell--today-checked {
  border-color: rgba(255, 214, 93, .72) !important;
  background:
    radial-gradient(circle at 50% 0%, rgba(255, 236, 170, .28), transparent 55%),
    radial-gradient(circle at 50% 100%, rgba(120, 210, 140, .16), transparent 52%),
    linear-gradient(165deg, rgba(52, 62, 36, .94) 0%, rgba(16, 14, 10, .98) 100%) !important;
  box-shadow:
    inset 0 1px 0 rgba(255, 236, 170, .22),
    0 0 0 1px rgba(120, 210, 140, .22),
    0 0 20px rgba(231, 194, 93, .2) !important;
}

@media (max-width: 900px) {
  .bg-att-cell__inner {
    min-height: 58px;
    gap: 4px;
    padding: 6px 3px;
  }

  .bg-att-cell__check-box {
    width: 26px;
    height: 26px;
    border-radius: 7px;
  }

  .bg-att-cell__check-box svg {
    width: 15px;
    height: 15px;
  }

  .bg-att-cell__check-text {
    font-size: 9px;
  }
}
</style>
